import { z } from "zod";
import type { AcCardStatus } from "./accreditation";
import {
	type AccreditationFormMessages,
	type AccreditationSubmitField,
	EMAIL_MAX,
	type MealRequirement,
	PHONE_MAX,
	type WheelchairType,
	enAccreditationFormMessages,
	makePatchCardFields,
	makePatchPersonFields,
} from "./accreditation-write";
import type { FileKind } from "./files";
import { type TravelFormMessages, type TravelLeg, departureNotBeforeArrival, enTravelFormMessages, makeTravelLegFields } from "./travel";

/**
 * A participant finishes their own registration (user, 2026-11-04):
 * `/me/registration/:acCardId`, active role PARTICIPANT, their OWN card only
 * (`people.user_id` = the login), while the card is DRAFT or RETURN.
 *
 * What the person may fill in:
 *   - step 3 "Additional": contact (email + mobile REQUIRED), emergency
 *     contact, medical & meals, the media organisation (media categories),
 *     the arrival / departure legs;
 *   - step 5 "Card": the name on the card, the name in results, and the
 *     photo / TV photo (through `/files`, kinds `PARTICIPANT_FILE_KINDS`);
 *   - the two declarations (`PARTICIPANT_DECLARATION_FIELDS`), made by the
 *     person on every submit (user, 2026-11-04: they are about the person).
 * Never identity, ID documents, the chain (organisation / category /
 * function), the sport profile, or supporting / health documents.
 */

/** The file kinds a participant may upload / replace / remove on their own card (while DRAFT / RETURN), and read at any status. */
export const PARTICIPANT_FILE_KINDS = ["PHOTO", "TV_PHOTO"] as const satisfies readonly FileKind[];
export type ParticipantFileKind = (typeof PARTICIPANT_FILE_KINDS)[number];
export const isParticipantFileKind = (k: string): k is ParticipantFileKind => (PARTICIPANT_FILE_KINDS as readonly string[]).includes(k);

/**
 * The declarations the person makes themselves, at submit (user, 2026-11-04):
 * the submit body carries both as `true` and the submit statement records
 * them on the person, so they never hold back `can_submit` on the read.
 */
export const PARTICIPANT_DECLARATION_FIELDS = ["consent_truthfulness", "consent_pdpa"] as const satisfies readonly AccreditationSubmitField[];
export type ParticipantDeclarationField = (typeof PARTICIPANT_DECLARATION_FIELDS)[number];

/** The `people` columns of step 3 the person may write (`person` in the PATCH). */
export const PARTICIPANT_REGISTRATION_PERSON_FIELDS = [
	"email_address",
	"phone_country_code",
	"phone_number",
	"contact_name",
	"country_of_residence",
	"residential_address",
	"emergency_contact_name",
	"emergency_contact_relationship",
	"emergency_phone_country_code",
	"emergency_phone_number",
	"blood_group",
	"food_type",
	"allergic",
	"medical_condition",
	"wheelchair_user",
	"wheelchair_type",
] as const;
export type ParticipantRegistrationPersonField = (typeof PARTICIPANT_REGISTRATION_PERSON_FIELDS)[number];

/** The `ac_cards` columns the person may write: the step-5 names and the step-3 media block. */
export const PARTICIPANT_REGISTRATION_CARD_FIELDS = [
	"accreditation_name",
	"tv_short_name",
	"employer_media_organization",
	"original_job",
	"employer_phone_country_code",
	"employer_phone_number",
	"employer_email_address",
	"employer_address",
] as const;
export type ParticipantRegistrationCardField = (typeof PARTICIPANT_REGISTRATION_CARD_FIELDS)[number];

/** A face file of the card, presigned like the profile photo (cacheable window). */
export interface ParticipantRegistrationPhoto {
	file_id: string;
	url: string;
	thumb_url: string | null;
	expires_at: string;
}

/** `GET /me/registration/:acCardId` (and the answer of its PATCH / submit). */
export interface ParticipantRegistration {
	ac_card_id: string;
	/** `REG-<org>-000123` / `ACC-<org>-000123`. */
	card_no: string;
	status: AcCardStatus;
	/** The card's version: send it back with PATCH / submit (→ 409 STALE_VERSION when it moved). */
	version: number;
	/** The person may change this card now (DRAFT / RETURN). */
	editable: boolean;
	/** The person may submit now: editable, a registration window allows a first submit, nothing missing but the declarations (made at submit). */
	can_submit: boolean;
	/** For a DRAFT: registration windows allow the first submit now (a RETURN resubmit is never window-bound → true). */
	window_open: boolean;
	/** What submit still needs (`ACCREDITATION_SUBMIT_REQUIRED` names, the staff submit's rule). Some are not the person's to fill (ID, sport…): their organisation must. The declarations are the person's, at submit. */
	missing: AccreditationSubmitField[];
	/** While RETURN: the host's remark saying what to change (user, 2026-11-04: the person sees it). Null otherwise. */
	return_remark: string | null;
	games: { games_id: string; code: string; name: string; timezone: string };
	organization: { name: string; code: string | null };
	category: { name: string; code: string; card_color: string | null };
	sub_function: string | null;
	sport: string | null;
	/** The organisation is of the MEDIA type: the form shows the media-organisation block. */
	is_media: boolean;
	/** Read-only name parts, for the card-name / TV-name suggestions. */
	names: { first_middle_name: string | null; last_surname: string | null; has_last_surname: boolean; written_name_in_identification: string | null };
	person: {
		email_address: string | null;
		phone_country_code: string | null;
		phone_number: string | null;
		contact_name: string | null;
		country_of_residence: string | null;
		residential_address: string | null;
		emergency_contact_name: string | null;
		emergency_contact_relationship: string | null;
		emergency_phone_country_code: string | null;
		emergency_phone_number: string | null;
		blood_group: string | null;
		food_type: MealRequirement[] | null;
		allergic: string | null;
		medical_condition: string | null;
		wheelchair_user: boolean;
		wheelchair_type: WheelchairType | null;
	};
	card: Record<ParticipantRegistrationCardField, string | null>;
	/** The card's legs, the record's `TravelLeg` shape (null = none). */
	travel: { arrival: TravelLeg | null; departure: TravelLeg | null };
	photo: ParticipantRegistrationPhoto | null;
	tv_photo: ParticipantRegistrationPhoto | null;
}

type M = AccreditationFormMessages;

const requiredEmail = (m: M) => z.string({ required_error: m.required }).trim().min(1, m.required).max(EMAIL_MAX, m.textTooLong).email(m.emailInvalid);
const requiredText = (m: M, max: number) => z.string({ required_error: m.required }).trim().min(1, m.required).max(max, m.textTooLong);

/** The person's contact essentials, required and non-blank (user, 2026-11-04): both PATCHes of a participant use them. */
export function participantRequiredContact(m: M = enAccreditationFormMessages) {
	return {
		email_address: requiredEmail(m),
		phone_country_code: requiredText(m, 8),
		phone_number: requiredText(m, PHONE_MAX),
	};
}

/**
 * `PATCH /me/registration/:acCardId`. `version` = the card's version from
 * the read. `person` is required and carries the three required contact
 * fields every time; every other key is optional (omitted = unchanged,
 * blank / null = clear). `arrival` / `departure`: the whole leg, `null`
 * removes it, omitted leaves it (a leg sent back unchanged writes nothing).
 * Unknown keys are refused (422): identity, chain, sport and documents are
 * not the person's.
 */
export function makeParticipantRegistrationForm(m: M = enAccreditationFormMessages, t: TravelFormMessages = enTravelFormMessages) {
	const person = makePatchPersonFields(m)
		.pick({
			contact_name: true,
			country_of_residence: true,
			residential_address: true,
			emergency_contact_name: true,
			emergency_contact_relationship: true,
			emergency_phone_country_code: true,
			emergency_phone_number: true,
			blood_group: true,
			food_type: true,
			allergic: true,
			medical_condition: true,
			wheelchair_user: true,
			wheelchair_type: true,
		})
		.extend(participantRequiredContact(m))
		.strict();
	const card = makePatchCardFields(m).pick({
		accreditation_name: true,
		tv_short_name: true,
		employer_media_organization: true,
		original_job: true,
		employer_phone_country_code: true,
		employer_phone_number: true,
		employer_email_address: true,
		employer_address: true,
	});
	return z
		.object({
			version: z.number({ required_error: m.required }).int().min(1),
			person,
			...card.shape,
			arrival: makeTravelLegFields(t).nullable().optional(),
			departure: makeTravelLegFields(t).nullable().optional(),
		})
		.strict()
		.refine(departureNotBeforeArrival, { path: ["departure", "travel_date"], message: t.departureBeforeArrival });
}
export const participantRegistrationBody = makeParticipantRegistrationForm();
export type ParticipantRegistrationBody = z.input<typeof participantRegistrationBody>;

/**
 * `POST /me/registration/:acCardId/submit`. No reason: Submit is exempt
 * (`AC_CARD_REASON_EXEMPT_ACTIONS`). Both declarations must be `true`: the
 * person makes them on every submit, a RETURN resubmit included.
 */
export const participantSubmitBody = z
	.object({ version: z.number().int().min(1), consent_truthfulness: z.literal(true), consent_pdpa: z.literal(true) })
	.strict();
export type ParticipantSubmitBody = z.input<typeof participantSubmitBody>;
